import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { CustomerDetails } from 'src/app/models/basket';
import { GiftCard, GiftCardType } from 'src/app/models/gift-card';
import { CheckoutService } from 'src/app/services/checkout/checkout.service';
import { CustomerDetailService } from 'src/app/services/checkout/customer-details.service';
import { ContentService } from 'src/app/services/content/content.service';
import { ShippingService } from 'src/app/services/shipping';
import {
  CenterSectionComponent,
  PaymentOptionsComponent,
  TextButtonComponent,
  TextInputComponent,
  ToggleComponent,
} from 'src/app/shared/components';
import { TypedFormGroup, buildFlatFormGroup } from 'src/app/shared/forms';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { environment } from 'src/environments/environment';
import { DisplayCurrencyPipe } from '../../../shared/pipes/display-currency.pipe';
import { SanitizeHtmlPipe } from '../../../shared/pipes/sanitize-html.pipe';
import { GiftCardDeliveryComponent } from './gift-card-delivery/gift-card-delivery.component';

@Component({
  selector: 'app-gift-card',
  templateUrl: './gift-card.component.html',
  styleUrls: ['./gift-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CenterSectionComponent,
    SanitizeHtmlPipe,
    ReactiveFormsModule,
    MatRadioModule,
    NgSelectComponent,
    NgOptionComponent,
    DisplayCurrencyPipe,
    TextInputComponent,
    TextButtonComponent,
    ToggleComponent,
    RouterLink,
    PaymentOptionsComponent,
    GiftCardDeliveryComponent,
  ],
})
export class GiftCardComponent extends ContentSection implements OnInit {
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private customerDetailService = inject(CustomerDetailService);
  private shippingService = inject(ShippingService);
  private checkoutService = inject(CheckoutService);

  contentUrl = environment.cmsOrigin;
  currency = environment.i18n.currency;
  pageNotFoundUrl = environment.urls.page_not_found;
  tradeCondtionsLink = `/${environment.urls.content.trade_conditions}`;

  bannerImage: string;
  title: string;
  description: string;

  senderTitle: string;
  senderDescription: string;
  recipientTitle: string;
  nameLabel: string;
  namePlaceholder: string;
  addToBasketButton: string;

  giftContent: {
    type: string;
    valueLabel: string;
    valuePlaceholder: string;
    valueClarification: string;
    emailLabel: string;
    emailPlaceholder: string;
    accompanyingMessageLabel: string;
    accompanyingMessagePlaceholder: string;
  };

  punchContent: {
    type: string;
    valueLabel: string;
    valuePlaceholder: string;
    valueClarification: string;
    emailLabel: string;
    emailPlaceholder: string;
    accompanyingMessageLabel: string;
    accompanyingMessagePlaceholder: string;
  };

  overviewTitle: string;
  overview: {
    name: string;
    pricePerUnit: string;
    pricePerPunch: string;
    amount: string;
    totalPrice: string;
    totalSummary: string;
    shippingPriceLabel: string;
    VATLabel: string;
  };

  enabled: boolean;
  form!: TypedFormGroup<GiftCard>;
  GiftCardType = GiftCardType;
  minimumGiftCardAmount: number;
  giftCardMonths: number;
  shippingPrice: number;

  deliveryForm!: TypedFormGroup<CustomerDetails>;
  termsOfServiceLabel: string;
  termsOfServiceLinkLabel: string;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'giftcard', { meta: true });

    const bannerImage = this.content.get('bannerImage');
    if (bannerImage) {
      this.bannerImage = `${this.contentUrl}/${bannerImage}`;
    }
    this.title = this.content.get('titleText');
    this.description = this.contentService.addImageSource(this.content.get('descriptionText'));

    this.senderTitle = this.content.get('senderTitleText');
    this.senderDescription = this.content.get('senderDescriptionText');
    this.recipientTitle = this.content.get('recipientTitleText');
    this.nameLabel = this.content.get('nameLabelText');
    this.namePlaceholder = this.content.get('namePlaceholderText');
    this.addToBasketButton = this.content.get('addToBasketButton');

    const giftCardType = this.content.get('giftCardText');
    this.giftContent = {
      type: giftCardType,
      valueLabel: this.content.get('valueLabelText')?.replaceAll('{type}', giftCardType),
      valuePlaceholder: this.content.get('valuePlaceholderText')?.replaceAll('{type}', giftCardType),
      valueClarification: this.content.get('valueClarificationText')?.replaceAll('{type}', giftCardType),
      emailLabel: this.content.get('emailLabelText')?.replaceAll('{type}', giftCardType),
      emailPlaceholder: this.content.get('emailPlaceholderText')?.replaceAll('{type}', giftCardType),
      accompanyingMessageLabel: this.content.get('accompanyingMessageLabelText')?.replaceAll('{type}', giftCardType),
      accompanyingMessagePlaceholder: this.content.get('accompanyingMessagePlaceholderText')?.replaceAll('{type}', giftCardType),
    };

    const punchCardType = this.content.get('punchCardText');
    this.punchContent = {
      type: punchCardType,
      valueLabel: this.content.get('valueLabelText')?.replaceAll('{type}', punchCardType),
      valuePlaceholder: this.content.get('valuePlaceholderText')?.replaceAll('{type}', punchCardType),
      valueClarification: this.content.get('valueClarificationText')?.replaceAll('{type}', punchCardType),
      emailLabel: this.content.get('emailLabelText')?.replaceAll('{type}', punchCardType),
      emailPlaceholder: this.content.get('emailPlaceholderText')?.replaceAll('{type}', punchCardType),
      accompanyingMessageLabel: this.content.get('accompanyingMessageLabelText')?.replaceAll('{type}', punchCardType),
      accompanyingMessagePlaceholder: this.content.get('accompanyingMessagePlaceholderText')?.replaceAll('{type}', punchCardType),
    };

    this.overviewTitle = this.content.get('overviewTitle');
    this.overview = {
      name: this.content.get('overviewNameText'),
      pricePerUnit: this.content.get('overviewPricePerUnit'),
      pricePerPunch: this.content.get('overviewPricePerPunch'),
      amount: this.content.get('overviewAmount'),
      totalPrice: this.content.get('overviewTotalPrice'),
      totalSummary: this.content.get('overviewTotalSummary'),
      shippingPriceLabel: this.content.get('shippingPriceLabel') || 'Gebyr',
      VATLabel: this.content.get('vATLabel') || 'Heraf Moms',
    };

    // Delivery content
    const basketContent = this.contentService.get('basketShipping');
    this.termsOfServiceLabel = basketContent.get('termsOfServiceTextText');
    this.termsOfServiceLinkLabel = basketContent.get('termsOfServiceLinkText');
  }

  ngOnInit() {
    const merchant = this.contentService.getMerchant();
    this.minimumGiftCardAmount = merchant.minimumGiftCardAmount;
    this.giftCardMonths = merchant.giftCardMonths;

    this.giftContent.valueClarification = this.giftContent.valueClarification
      ?.replaceAll('{giftCardPrice}', `${this.minimumGiftCardAmount}`)
      ?.replaceAll('{giftCardMonths}', `${this.giftCardMonths}`);
    this.giftContent.valuePlaceholder = this.giftContent.valuePlaceholder?.replaceAll('{giftCardPrice}', `${this.minimumGiftCardAmount}`);

    this.punchContent.valueClarification = this.punchContent.valueClarification
      ?.replaceAll('{giftCardPrice}', `${this.minimumGiftCardAmount}`)
      ?.replaceAll('{giftCardMonths}', `${this.giftCardMonths}`);
    this.punchContent.valuePlaceholder = this.punchContent.valuePlaceholder?.replaceAll('{giftCardPrice}', `${this.minimumGiftCardAmount}`);

    this.deliveryForm = this.customerDetailService.getForm();
    this.form = this.createForm({
      type: GiftCardType.Gift,
      value: undefined,
      recipientName: undefined,
      recipientEmail: undefined,
      accompanyingMessage: undefined,
      charges: undefined,
      chargesAmount: undefined,
    });

    const shipping = this.shippingService.getGiftCardShipping();
    this.shippingPrice = shipping?.price || 0;

    if (!merchant.enableGiftCards) {
      this.router.navigate([this.pageNotFoundUrl]);
    }
  }

  imageError() {
    this.bannerImage = '';
  }

  createForm(giftCard: GiftCard): TypedFormGroup<GiftCard> {
    const form = buildFlatFormGroup(giftCard, { updateOn: 'change' });

    const noDecimalRegex = /^\d*$/;
    form.controls.value.setValidators([
      Validators.required,
      Validators.min(this.minimumGiftCardAmount),
      Validators.pattern(noDecimalRegex),
    ]);
    form.controls.recipientName.setValidators([Validators.required]);
    form.controls.recipientEmail.setValidators([Validators.required, Validators.email]);
    form.controls.chargesAmount.setValidators([Validators.pattern(noDecimalRegex)]);

    return form;
  }

  checkoutGiftCard() {
    this.form.markAllAsTouched();
    this.deliveryForm.markAllAsTouched();

    if (this.form.value.type === GiftCardType.Punch) {
      this.form.controls.value.setValue(this.form.value.charges * this.form.value.chargesAmount);
    }

    if (!this.deliveryForm.controls.tradeAgreement.value) {
      return this.openSnackbar('Handelsbetingelserne er ikke accepteret');
    }

    if (!this.deliveryForm.valid) {
      return this.openSnackbar('Der mangler afsender information');
    }

    if (!this.form.valid) {
      if (this.form.value.value < this.minimumGiftCardAmount) {
        return this.openSnackbar(`Gavekort beløbet er for lavt, det skal mindst være på ${this.minimumGiftCardAmount} kr`);
      }
      return this.openSnackbar('Der mangler at blive udfyld modtager navn og/eller email');
    }

    const giftcard = this.form.value;
    if (this.form.value.type === GiftCardType.Gift) {
      giftcard.charges = undefined;
    }

    this.checkoutService.giftCardCheckout(giftcard);
  }

  private openSnackbar(message: string) {
    this.snackBar.open(message, 'luk', {
      duration: 8000,
      panelClass: 'warn',
    });
  }
}
